import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, X, Monitor, Maximize, Minimize } from 'lucide-react';
import { slides, talkingPoints } from '../data/webinarContent';
import TitleHookSlide from './slides/TitleHookSlide';
import NoBSPromiseSlide from './slides/NoBSPromiseSlide';
import MeetHostSlide from './slides/MeetHostSlide';
import MarketRealitySlide from './slides/MarketRealitySlide';
import ConversionCliffSlide from './slides/ConversionCliffSlide';
import CompetitorGapSlide from './slides/CompetitorGapSlide';
import FrankensteinSlide from './slides/FrankensteinSlide';
import AISolutionSlide from './slides/AISolutionSlide';
import OneSystemSlide from './slides/OneSystemSlide';
import CaseStudyProofSlide from './slides/CaseStudyProofSlide';
import PitchSlide from './slides/PitchSlide';
import LeadAuditSlide from './slides/LeadAuditSlide';
import QAFinalSlide from './slides/QAFinalSlide';
import TalkingPoints from './TalkingPoints';
export function WebinarPresentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [presenterMode, setPresenterMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const channel = new BroadcastChannel('webinar-sync');
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'SLIDE_CHANGE') {
        setCurrentSlide(event.data.slideIndex);
      }
    };
    channel.addEventListener('message', handleMessage);
    const savedSlide = localStorage.getItem('currentSlide');
    if (savedSlide) {
      setCurrentSlide(parseInt(savedSlide));
    }
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        changeSlide(currentSlide + 1);
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        changeSlide(currentSlide - 1);
      } else if (e.key === 'n' || e.key === 'N') {
        setShowNotes(!showNotes);
      } else if (e.key === 'f' || e.key === 'F') {
        setIsFullscreen(!isFullscreen);
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, showNotes, isFullscreen]);
  const changeSlide = (newSlide: number) => {
    setCurrentSlide(newSlide);
    localStorage.setItem('currentSlide', newSlide.toString());
    const channel = new BroadcastChannel('webinar-sync');
    channel.postMessage({
      type: 'SLIDE_CHANGE',
      slideIndex: newSlide
    });
    channel.close();
  };
  const openPresenterMode = () => {
    localStorage.setItem('currentSlide', currentSlide.toString());
    const baseUrl = window.location.origin + window.location.pathname;
    const notesUrl = baseUrl + '?view=presenter';
    const notesWindow = window.open(notesUrl, 'presenter-notes', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
    if (notesWindow) {
      setPresenterMode(true);
      setShowNotes(false);
    } else {
      alert('Please allow pop-ups to use Presenter Mode');
    }
  };
  const renderSlide = () => {
    const slide = slides[currentSlide];
    switch (slide.type) {
      case 'title-hook':
        return <TitleHookSlide content={slide.content} data-id="element-253" />;
      case 'no-bs-promise':
        return <NoBSPromiseSlide content={slide.content} data-id="element-254" />;
      case 'meet-host':
        return <MeetHostSlide content={slide.content} data-id="element-255" />;
      case 'market-reality':
        return <MarketRealitySlide content={slide.content} data-id="element-256" />;
      case 'conversion-cliff':
        return <ConversionCliffSlide content={slide.content} data-id="element-257" />;
      case 'competitor-gap':
        return <CompetitorGapSlide content={slide.content} data-id="element-258" />;
      case 'frankenstein':
        return <FrankensteinSlide content={slide.content} data-id="element-259" />;
      case 'ai-solution':
        return <AISolutionSlide content={slide.content} data-id="element-260" />;
      case 'one-system':
        return <OneSystemSlide content={slide.content} data-id="element-261" />;
      case 'case-study-proof':
        return <CaseStudyProofSlide content={slide.content} data-id="element-262" />;
      case 'pitch':
        return <PitchSlide content={slide.content} data-id="element-263" />;
      case 'lead-audit':
        return <LeadAuditSlide content={slide.content} data-id="element-264" />;
      case 'qa-final':
        return <QAFinalSlide content={slide.content} data-id="element-265" />;
      default:
        return <div data-id="element-266">Slide not found</div>;
    }
  };
  const currentTalkingPoints = talkingPoints.find(tp => tp.slideId === slides[currentSlide].id);
  return <div className="w-full h-screen bg-[#1C1C1C] flex flex-col relative" data-id="element-267">
      {/* Header - hidden in fullscreen */}
      {!isFullscreen && <div className="bg-[#1C1C1C] border-b border-[#4D4D4D] px-6 py-4 flex items-center justify-between" data-id="element-268">
          <div className="flex items-center gap-4" data-id="element-269">
            <div className="text-[#0FB07D] font-bold text-xl" data-id="element-270">VIRIDIA</div>
            <div className="text-[#F5F5F5] text-sm" data-id="element-271">
              Home Services Growth Blueprint
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-272">
            <div className="text-[#4D4D4D] text-sm" data-id="element-273">
              Slide {currentSlide + 1} / {slides.length}
            </div>

            <button onClick={() => setIsFullscreen(true)} className="flex items-center gap-2 px-4 py-2 bg-[#4D4D4D] text-white rounded-lg hover:bg-[#017C6D] transition-colors" title="Toggle fullscreen (F key)" data-id="element-274">
              <Maximize size={16} data-id="element-275" />
              Fullscreen
            </button>

            <button onClick={openPresenterMode} className="flex items-center gap-2 px-4 py-2 bg-[#0FB07D] text-white rounded-lg hover:bg-[#017C6D] transition-colors" title="Open presenter notes in separate window" data-id="element-276">
              <Monitor size={16} data-id="element-277" />
              Presenter Mode
            </button>

            {!presenterMode && <button onClick={() => setShowNotes(!showNotes)} className="flex items-center gap-2 px-4 py-2 bg-[#017C6D] text-white rounded-lg hover:bg-[#0FB07D] transition-colors" data-id="element-278">
                <MessageSquare size={16} data-id="element-279" />
                {showNotes ? 'Hide' : 'Show'} Notes
              </button>}
          </div>
        </div>}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-280">
        <div className="flex-1 flex items-center justify-center p-8 overflow-auto" data-id="element-281">
          <div className="w-full max-w-6xl" data-id="element-282">{renderSlide()}</div>
        </div>

        {showNotes && !presenterMode && !isFullscreen && currentTalkingPoints && <div className="w-96 bg-[#1C1C1C] border-l border-[#4D4D4D] overflow-auto" data-id="element-283">
              <div className="p-6" data-id="element-284">
                <div className="flex items-center justify-between mb-4" data-id="element-285">
                  <h3 className="text-[#F5F5F5] font-semibold" data-id="element-286">
                    Speaker Notes
                  </h3>
                  <button onClick={() => setShowNotes(false)} className="text-[#4D4D4D] hover:text-[#F5F5F5]" data-id="element-287">
                    <X size={20} data-id="element-288" />
                  </button>
                </div>
                <TalkingPoints talkingPoint={currentTalkingPoints} data-id="element-289" />
              </div>
            </div>}
      </div>

      {/* Navigation Footer - hidden in fullscreen */}
      {!isFullscreen && <div className="bg-[#1C1C1C] border-t border-[#4D4D4D] px-6 py-4 flex items-center justify-between" data-id="element-290">
          <button onClick={() => changeSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} className="flex items-center gap-2 px-4 py-2 bg-[#4D4D4D] text-white rounded-lg hover:bg-[#017C6D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-291">
            <ChevronLeft size={20} data-id="element-292" />
            Previous
          </button>

          <div className="flex-1 mx-8" data-id="element-293">
            <div className="h-2 bg-[#4D4D4D] rounded-full overflow-hidden" data-id="element-294">
              <div className="h-full bg-[#0FB07D] transition-all duration-300" style={{
            width: `${(currentSlide + 1) / slides.length * 100}%`
          }} data-id="element-295" />
            </div>
          </div>

          <button onClick={() => changeSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} className="flex items-center gap-2 px-4 py-2 bg-[#0FB07D] text-white rounded-lg hover:bg-[#017C6D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-296">
            Next
            <ChevronRight size={20} data-id="element-297" />
          </button>
        </div>}

      {/* Fullscreen floating controls */}
      {isFullscreen && <>
          {/* Exit fullscreen button - top right */}
          <button onClick={() => setIsFullscreen(false)} className="absolute top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-[#1C1C1C]/80 backdrop-blur text-white rounded-lg hover:bg-[#1C1C1C] transition-colors border border-[#4D4D4D]/50" title="Exit fullscreen (Esc or F)" data-id="element-298">
            <Minimize size={16} data-id="element-299" />
            Exit
          </button>

          {/* Slide counter - top left */}
          <div className="absolute top-4 left-4 z-50 px-4 py-2 bg-[#1C1C1C]/80 backdrop-blur text-[#F5F5F5] rounded-lg text-sm border border-[#4D4D4D]/50" data-id="element-300">
            {currentSlide + 1} / {slides.length}
          </div>

          {/* Minimal nav arrows - bottom center */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4" data-id="element-301">
            <button onClick={() => changeSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} className="w-10 h-10 flex items-center justify-center bg-[#1C1C1C]/80 backdrop-blur text-white rounded-full hover:bg-[#017C6D] transition-colors disabled:opacity-30 border border-[#4D4D4D]/50" data-id="element-302">
              <ChevronLeft size={20} data-id="element-303" />
            </button>
            <button onClick={() => changeSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} className="w-10 h-10 flex items-center justify-center bg-[#1C1C1C]/80 backdrop-blur text-white rounded-full hover:bg-[#017C6D] transition-colors disabled:opacity-30 border border-[#4D4D4D]/50" data-id="element-304">
              <ChevronRight size={20} data-id="element-305" />
            </button>
          </div>
        </>}

      {/* Keyboard Shortcuts Hint - hidden in fullscreen */}
      {!isFullscreen && <div className="absolute bottom-20 left-6 text-[#4D4D4D] text-xs" data-id="element-306">
          ← → Navigate • N Notes • F Fullscreen • Presenter Mode for separate
          notes window
        </div>}
    </div>;
}